import type { DentalOfficeShiftCardMenuItem } from '@tempmee/react-blocks';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import browserHistory from '~/browserHistory';
import { addFavoriteProfessional, removeFavoriteProfessional } from '~/actions';
import { useReportIncidentItem } from '~/components/JobList/TodayJobs/ReportIncidentPopup/hooks';

interface IUseCurrentShiftCardMenuArgs {
  /** Shift id — chat route + report submission. */
  jobId: string;
  /** Shift start datetime — drives the 30-min report-incident window. */
  shiftStart?: string | Date;
  /** Assigned professional's user id — favorite toggle. */
  professionalId?: string;
  /** Assigned professional's full name — shown in the report-incident popup. */
  professionalName?: string;
  /** Whether an incident was already reported for this shift. */
  alreadyReported?: boolean;
}

/**
 * Assembles the "current shift" / "today's professional" card actions shared by the
 * Schedule List and the dashboard "Today's Professionals" card (GOTU-4588):
 * - `menuItems`: Chat, Add/Remove Favorite (Rebook is added by the caller's rebook flow)
 * - report-incident wiring for the DS card's warning icon (`onReportIncident` opens the
 *   no-show/walk-out selection popup; `incidentReported` / `canReportIncident` reflect state)
 *
 * Mount a single `<ReportIncidentPopup />` in the list that renders these cards.
 */
export const useCurrentShiftCardMenu = ({
  jobId,
  shiftStart,
  professionalId,
  professionalName,
  alreadyReported = false,
}: IUseCurrentShiftCardMenuArgs) => {
  const dispatch = useDispatch();

  const favoriteProfessionals = useSelector(
    // `favoriteProfessionals` holds professional ids at runtime, though typed as IUser[].
    (state: { user: { user?: { favoriteProfessionals?: unknown } } }) =>
      (state.user.user?.favoriteProfessionals ?? []) as string[],
  );
  const isFavorite = !!professionalId && favoriteProfessionals.includes(professionalId);

  // The report hook reads job.shift.start / job.professional.full_name / job.incident.
  const reportJob = useMemo(
    () => ({
      id: jobId,
      incident: alreadyReported || undefined,
      shift: { start: shiftStart },
      professional: { full_name: professionalName },
    }),
    [jobId, alreadyReported, shiftStart, professionalName],
  );
  const { isReported, canReport, onReportIncidentClick } = useReportIncidentItem(reportJob);

  const toggleFavorite = useCallback(() => {
    if (!professionalId) return;
    dispatch(
      isFavorite
        ? removeFavoriteProfessional(professionalId)
        : addFavoriteProfessional(professionalId),
    );
  }, [dispatch, isFavorite, professionalId]);

  const menuItems = useMemo<DentalOfficeShiftCardMenuItem[]>(() => {
    const items: DentalOfficeShiftCardMenuItem[] = [
      { label: 'Chat', onSelect: () => browserHistory.push(`/dashboard/chat/${jobId}`) },
    ];
    if (professionalId) {
      items.push({
        label: isFavorite ? 'Remove from Favorites' : 'Add to Favorites',
        onSelect: toggleFavorite,
      });
    }
    return items;
  }, [jobId, professionalId, isFavorite, toggleFavorite]);

  return {
    menuItems,
    onReportIncident: onReportIncidentClick,
    incidentReported: isReported,
    canReportIncident: canReport,
  };
};
